import React from 'react';
import { getFuelGradeSignature } from '@shared/utils/consumptionSetIdentity';
import { trimString } from '@shared/utils/string';
import type { ConsumptionSetProps } from '@api/models/consumption-set';
import type { PerformanceEntryProps } from '@api/models/performance-entry';
import type { SelectOption } from '@client/components/Select';
import styles from './renderConsumptionSetOptions.module.css';

const MAX_NAME_LENGTH = 20;

type ConsumptionSetOptionSource = Pick<
	ConsumptionSetProps,
	'id' | 'name' | 'ladenSpeed' | 'ballastSpeed'
> & {
	PerformanceEntries?: Array<Pick<
		PerformanceEntryProps,
		'relation' | 'fuelZoneType' | 'fuelType' | 'condition'
	>> | null;
};

const formatSpeed = (speed: number | null | undefined) => {
	if (speed == null || Number.isNaN(Number(speed))) {
		return '—';
	}

	const value = Number(speed);

	return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const formatFuelGrades = (
	set: ConsumptionSetOptionSource,
): string => {
	const { normal, eca } = getFuelGradeSignature(set.PerformanceEntries);

	return `N: ${normal ?? '—'} / E: ${eca ?? '—'}`;
};

export const getConsumptionSetDisplayName = (
	set: Pick<ConsumptionSetProps, 'name' | 'ladenSpeed'>,
): string => {
	if (set.name) {
		return set.name;
	}

	return `Generated (${Number(set.ladenSpeed).toFixed(2)} kts)`;
};

export const renderConsumptionSetOptions = (
	consumptionSets: Array<ConsumptionSetOptionSource>,
): SelectOption[] => consumptionSets.map((set) => {
	const fullName = getConsumptionSetDisplayName(set);
	const truncatedName = trimString(fullName, MAX_NAME_LENGTH, true) ?? fullName;
	const ballast = formatSpeed(set.ballastSpeed);
	const laden = formatSpeed(set.ladenSpeed);
	const fuelGrades = formatFuelGrades(set);
	const speeds = `(B: ${ballast} / L: ${laden})`;
	const title = `${fullName} ${fuelGrades} ${speeds}`;

	return {
		value: set.id,
		title,
		label: (
			<span className={styles.optionLabel} title={title}>
				<span className={styles.name}>{truncatedName}</span>
				<span className={styles.grades}>{fuelGrades}</span>
				<strong className={styles.speeds}>{speeds}</strong>
			</span>
		),
	};
});

export default renderConsumptionSetOptions;
